import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Volume2, VolumeX, Reply } from 'lucide-react';
import { toast } from 'sonner';
import { getFlowNotifications, markFlowNotificationsRead, dismissFlowNotifications, sendFlowNotification } from '../services/taskApi';

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1174, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  } catch { /* audio unavailable */ }
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const NotificationBell = ({ isDark }) => {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [sound, setSound] = useState(() => localStorage.getItem('notif_sound') !== 'off');
  const prevUnread = useRef(0);
  const soundRef = useRef(sound);
  soundRef.current = sound;

  const poll = useCallback(async () => {
    try {
      const res = await getFlowNotifications();
      const u = res.data.unread || 0;
      setNotifs(res.data.notifications || []);
      setUnread(u);
      if (u > prevUnread.current && soundRef.current) playBeep();
      prevUnread.current = u;
    } catch { /* not logged in / network */ }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [poll]);

  const openPanel = async () => {
    setOpen(true);
    if (unread > 0) {
      try {
        await markFlowNotificationsRead([]);
        setUnread(0);
        prevUnread.current = 0;
      } catch { /* ignore */ }
    }
  };

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    localStorage.setItem('notif_sound', next ? 'on' : 'off');
  };

  return (
    <>
      <button
        onClick={() => (open ? setOpen(false) : openPanel())}
        data-testid="notification-bell"
        className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 ${
          isDark ? 'bg-blue-600 text-white shadow-blue-500/30' : 'bg-blue-600 text-white shadow-blue-500/40'
        }`}
        title="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50" data-testid="notification-panel">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className={`absolute right-0 top-0 bottom-0 w-full sm:w-96 flex flex-col shadow-2xl ${
            isDark ? 'bg-[#0c1222] border-l border-white/10' : 'bg-white border-l border-gray-200'
          }`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h3 className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Notifications</h3>
              <div className="flex items-center gap-2">
                <button onClick={toggleSound} data-testid="notification-sound-toggle"
                  className={`p-1.5 rounded-lg ${isDark ? 'text-white/50 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}
                  title={sound ? 'Sound on — click to mute' : 'Sound off — click to unmute'}>
                  {sound ? <Volume2 size={16} /> : <VolumeX size={16} />}
                </button>
                <button onClick={() => setOpen(false)} data-testid="notification-close"
                  className={`p-1.5 rounded-lg ${isDark ? 'text-white/50 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}>
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {notifs.length === 0 ? (
                <div className={`text-center py-10 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  <Bell className="mx-auto mb-2 opacity-30" size={28} />
                  No notifications yet
                </div>
              ) : notifs.map(n => (
                <div key={n.id} data-testid={`notification-item-${n.id}`}
                  className={`p-3 rounded-xl border text-xs ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{n.title}</span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{timeAgo(n.created_at)}</span>
                      <button
                        onClick={async () => {
                          try { await dismissFlowNotifications([n.id]); setNotifs(p => p.filter(x => x.id !== n.id)); }
                          catch { toast.error('Failed to dismiss'); }
                        }}
                        className={`p-0.5 rounded ${isDark ? 'text-white/40 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                        title="Dismiss — actioned / clear from my list" data-testid={`notification-dismiss-${n.id}`}>
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                  {n.so_number && <div className="text-[10px] text-blue-400 font-medium mt-0.5">{n.so_number}{n.item_name ? ` — ${n.item_name}` : ''}</div>}
                  <p className={`mt-1 whitespace-pre-wrap ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{n.message}</p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>from {n.created_by}</span>
                    <button
                      onClick={async () => {
                        const txt = window.prompt(`Reply to ${n.created_by}${n.so_number ? ` about ${n.so_number}` : ''}:`, '');
                        if (!txt || !txt.trim()) return;
                        try {
                          const payload = n.created_by_sub && n.created_by_sub !== 'system'
                            ? { to_user_sub: n.created_by_sub }
                            : { to_role: 'admin' };
                          await sendFlowNotification({ ...payload, message: txt.trim(), title: `Reply: ${n.title}`, so_number: n.so_number || '', item_id: n.item_id || '', item_name: n.item_name || '' });
                          toast.success('Reply sent');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reply'); }
                      }}
                      className="flex items-center gap-1 text-[10px] font-medium text-blue-400 hover:text-blue-300"
                      data-testid={`notification-reply-${n.id}`}>
                      <Reply size={11} /> Reply
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
